/* eslint-disable react/no-unescaped-entities */

import type React from 'react';
import { useState, useEffect } from 'react';

import { useHandlePostulantInvitations } from '../hooks/useHandlePostulantInvitations';

import { Button } from '@/components';
import { PaginatorAsync } from '@/components/PaginatorAsync';
import useTourney from '@/hooks/useTourney';
import { useUsersProfilePic } from '@/hooks/useUsersProfilePic';
import { tournamentUtilService } from '@/services/tournamentUtilService';
import type { UsuarioDTReducidoTO } from '@/types/invites';
import { cn } from '@/utils';

interface RecommendedUsersProps {
  onInviteById: (userId: number) => Promise<boolean | undefined>;
  suggestUsersService: (cantMaxResultados?: any) => Promise<any>;
  type: string;
  idTorneo: number;
}

const RecommendedUsers: React.FC<RecommendedUsersProps> = ({
  onInviteById,
  suggestUsersService,
  type,
  idTorneo,
}) => {
  const [currentItems, setCurrentItems] = useState<UsuarioDTReducidoTO[]>([]);
  const [allUsers, setAllUsers] = useState<UsuarioDTReducidoTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 5;
  const { tourneyData } = useTourney();
  const { getStatusValidation } = tournamentUtilService(type, tourneyData);
  const { handleInvite, invitedUsers, setMember, memberUsers } =
    useHandlePostulantInvitations({
      type,
      idTorneo,
      onInvite: onInviteById,
      tourneyData,
    });

  const { setProfilePic } = useUsersProfilePic();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await suggestUsersService(10);
        setMember(false);

        // Simplificamos la lógica para manejar la respuesta
        if (response && response.estado === true) {
          // Si la respuesta tiene usuariosSugeridos, usamos ese array
          if (
            response.usuariosSugeridos &&
            Array.isArray(response.usuariosSugeridos)
          ) {
            setAllUsers(response.usuariosSugeridos);
          }
          // Si la respuesta tiene usuarios, usamos ese array
          else if (response.usuarios && Array.isArray(response.usuarios)) {
            setAllUsers(response.usuarios);
          }
          // Si no tiene ninguno de los dos, es un error
          else {
            console.error('❌ Respuesta sin usuarios:', response);
            throw new Error('La respuesta no contiene usuarios');
          }
        } else {
          console.error('❌ Respuesta con estado false:', response);
          throw new Error(response.mensaje || 'Error al obtener usuarios');
        }
      } catch (err) {
        console.error('❌ Error al cargar usuarios:', err);
        setError('Error al cargar usuarios recomendados');
        setAllUsers([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [suggestUsersService]);

  useEffect(() => {
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setCurrentItems(allUsers.slice(startIndex, endIndex));
  }, [currentPage, allUsers]);

  const handlePageChange = (selectedItem: { selected: number }) => {
    setCurrentPage(selectedItem.selected);
  };

  if (isLoading) {
    return <div>Cargando usuarios recomendados...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (allUsers.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg w-[427px]">
      <h1 className="text-[#333333] text-sm font-semibold mb-4">
        ¿No sabés a quién invitar? Elegí a uno de los DT's recomendados.
      </h1>
      <div className="bg-white rounded-lg">
        <div className="space-y-4 p-4">
          {currentItems.map((user) => (
            <div
              key={user.idUsuario}
              className="flex items-center justify-between py-2"
            >
              {setProfilePic({
                alt: user.apellido,
                dtName: user.nombres.split(' ')[0] + ' ' + user.apellido,
                url: user.fotoPerfil,
                size: 40,
              })}
              <div className="flex flex-col flex-1 ml-3">
                <p className="text-[#333333] font-bold leading-tight">{`${user.nombres} ${user.apellido}`}</p>
                <p className="text-[#717077] text-sm leading-tight">
                  {user.nombreEquipo}
                </p>
                <p className="text-[#717077] text-xs">{user.provincia}</p>
              </div>
              <Button
                className={cn('w-[115px] h-[36px] px-10 text-xs', {
                  'p-0':
                    (invitedUsers.size > 0 &&
                      invitedUsers.has(user.idUsuario)) ||
                    getStatusValidation(user.idUsuario, memberUsers) !==
                      'Invitar',
                })}
                disabled={
                  (invitedUsers.size > 0 && invitedUsers.has(user.idUsuario)) ||
                  getStatusValidation(user.idUsuario, memberUsers) !== 'Invitar'
                }
                type="button"
                variant="secondary"
                onClick={() => handleInvite(user.idUsuario, user.nombreEstado)}
              >
                {invitedUsers.size > 0 && invitedUsers.has(user.idUsuario)
                  ? 'Invitado'
                  : getStatusValidation(user.idUsuario, memberUsers)}
              </Button>
            </div>
          ))}
        </div>

        {allUsers.length > itemsPerPage && (
          <PaginatorAsync
            className="paginator-sidebar"
            currentPage={currentPage}
            pageCount={Math.ceil(allUsers.length / itemsPerPage)}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
};

export default RecommendedUsers;
